import { fetchEventSource } from "@microsoft/fetch-event-source";
import { fetchNewAccessToken } from "api/baseQuery";
import { resetAuth, setAuthTokens } from "features/authSlice";
import { AppDispatch } from "store";

interface FetchEventSourceOptions {
  url: string;
  token: string;
  refreshToken: string | null;
  dispatch: AppDispatch;
  onMessage: (data: any) => void;
  onError?: (error: any) => void;
  onDone?: () => void;
}

// --- debug logger, flip to false to silence ---
const SSE_DEBUG = true;
const log = (...args: unknown[]) => {
  if (SSE_DEBUG) {
    // eslint-disable-next-line no-console
    console.log(
      `%c[SSEManager ${new Date().toISOString()}]`,
      "color:#0aa",
      ...args,
    );
  }
};
const warn = (...args: unknown[]) => {
  if (SSE_DEBUG) {
    // eslint-disable-next-line no-console
    console.warn(
      `%c[SSEManager ${new Date().toISOString()}]`,
      "color:#e90",
      ...args,
    );
  }
};

const STALL_TIMEOUT_MS = 45_000;
const STALL_CHECK_INTERVAL_MS = 10_000;

export class SSEManagerClass {
  private static instance: SSEManagerClass | null = null;
  private abortController: AbortController | null = null;
  public isConnected = false;
  private currentUrl: string | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastMessageAt: number | null = null;
  private lastOpenAt: number | null = null;
  private stallCheckInterval: ReturnType<typeof setInterval> | null = null;
  private currentOptions: FetchEventSourceOptions | null = null;
  private messageCount = 0;
  private connectionAttempt = 0;

  static getInstance(): SSEManagerClass {
    if (!this.instance) {
      this.instance = new SSEManagerClass();
    }
    return this.instance;
  }

  connect(options: FetchEventSourceOptions) {
    const { url, token, refreshToken, onMessage, dispatch, onError, onDone } =
      options;

    this.connectionAttempt += 1;
    const attemptId = this.connectionAttempt;

    log(`connect() called [attempt #${attemptId}]`, {
      url,
      hasToken: !!token,
      tokenPrefix: token ? token.slice(0, 10) : null,
      hasRefreshToken: !!refreshToken,
      isConnected: this.isConnected,
      currentUrl: this.currentUrl,
      lastMessageAt: this.lastMessageAt,
      msSinceLastMessage: this.lastMessageAt
        ? Date.now() - this.lastMessageAt
        : null,
      storedLastNotificationId: localStorage.getItem("last_notification_id"),
    });

    if (this.isConnected && this.currentUrl === url) {
      warn(
        `connect() [attempt #${attemptId}] SKIPPED - already marked as connected to same URL. ` +
          `If notifications are actually stuck, this early-return is a likely culprit: ` +
          `isConnected can be stale/true even if the stream silently stopped delivering data.`,
        {
          msSinceLastMessage: this.lastMessageAt
            ? Date.now() - this.lastMessageAt
            : null,
          msSinceOpen: this.lastOpenAt ? Date.now() - this.lastOpenAt : null,
        },
      );
      return;
    }

    this.currentOptions = options;
    this.close("reconnect requested");
    this.isConnected = true;
    this.currentUrl = url;
    this.abortController = new AbortController();
    this.messageCount = 0;
    this.startStallWatchdog();

    const buildUrl = (baseUrl: string) => {
      const rawLastId = localStorage.getItem("last_notification_id");
      const isValidId =
        !!rawLastId && rawLastId !== "undefined" && rawLastId !== "null";

      if (rawLastId && !isValidId) {
        warn(
          "buildUrl() found INVALID last_notification_id in localStorage, clearing it",
          { rawLastId },
        );
        localStorage.removeItem("last_notification_id");
      }

      if (!isValidId) return baseUrl;

      const separator = baseUrl.includes("?") ? "&" : "?";
      const finalUrl = `${baseUrl}${separator}last_notification_id=${rawLastId}`;
      log("buildUrl() appending last_notification_id", { rawLastId, finalUrl });
      return finalUrl;
    };

    const startConnection = async (accessToken: string) => {
      if (this.abortController?.signal.aborted) {
        log(
          `startConnection [attempt #${attemptId}] aborted before start, bailing`,
        );
        return;
      }

      const fullUrl = buildUrl(url);
      log(`startConnection [attempt #${attemptId}] opening fetchEventSource`, {
        fullUrl,
      });

      try {
        await fetchEventSource(fullUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: this.abortController!.signal,
          onopen: async (response) => {
            log(`onopen [attempt #${attemptId}]`, {
              status: response.status,
              ok: response.ok,
              contentType: response.headers?.get?.("content-type"),
              headers: response.headers
                ? Object.fromEntries(response.headers.entries())
                : null,
            });

            const handleTokenRefresh = async () => {
              const currentRefreshToken = refreshToken;
              if (!currentRefreshToken) {
                warn(
                  `onopen [attempt #${attemptId}] 401 with no refresh token, resetting auth`,
                );
                dispatch(resetAuth());
                this.close("no refresh token on 401");
                return;
              }
              const newTokens = await fetchNewAccessToken(currentRefreshToken);
              if (newTokens?.access_token) {
                log(
                  `onopen [attempt #${attemptId}] token refreshed, reconnecting`,
                );
                const {
                  access_token: newAccessToken,
                  refresh_token: newRefreshToken,
                } = newTokens;
                dispatch(
                  setAuthTokens({
                    accessToken: newAccessToken,
                    refreshToken: newRefreshToken,
                  }),
                );
                startConnection(newAccessToken).catch((e) =>
                  warn("startConnection after token refresh failed", e),
                );
              } else {
                warn(
                  `onopen [attempt #${attemptId}] token refresh failed, resetting auth`,
                );
                dispatch(resetAuth());
                this.close("token refresh failed");
              }
            };

            if (response.ok) {
              this.isConnected = true;
              this.lastOpenAt = Date.now();
              this.lastMessageAt = Date.now(); // treat open as activity baseline

              const contentType = response.headers?.get?.("content-type") || "";
              if (!contentType.includes("text/event-stream")) {
                warn(
                  `onopen [attempt #${attemptId}] response.ok but content-type is NOT text/event-stream. ` +
                    `This strongly suggests a proxy/VPN/gateway is intercepting or transforming the stream ` +
                    `for this specific client.`,
                  { contentType },
                );
              }
            } else if (response.status === 401 && refreshToken) {
              await handleTokenRefresh();
              throw new Error("401");
            } else {
              warn(
                `onopen [attempt #${attemptId}] non-ok, non-401 response - stream will likely fail silently ` +
                  `or fetchEventSource will throw a generic error`,
                { status: response.status },
              );
            }
          },
          onmessage: (event) => {
            this.lastMessageAt = Date.now();
            this.messageCount += 1;

            try {
              if (!event.event) {
                log(
                  `onmessage [attempt #${attemptId}] no event.event name (likely keep-alive/comment)`,
                  {
                    id: event.id,
                    rawData: event.data,
                  },
                );
                return;
              }
              const data = JSON.parse(event.data);

              log(`onmessage [attempt #${attemptId}] #${this.messageCount}`, {
                eventName: event.event,
                eventId: event.id,
                data,
              });

              if (event.id) {
                localStorage.setItem("last_notification_id", event.id);
              } else {
                warn(
                  `onmessage [attempt #${attemptId}] event has a name but NO id - ` +
                    `NOT overwriting last_notification_id to avoid corrupting it`,
                  { eventName: event.event },
                );
              }

              onMessage({ data, event: event.event });
            } catch (e) {
              warn(
                `onmessage [attempt #${attemptId}] failed to parse/handle event`,
                e,
                {
                  rawEvent: event,
                },
              );
            }
          },
          onerror: (error) => {
            warn(`onerror [attempt #${attemptId}]`, error, {
              msSinceLastMessage: this.lastMessageAt
                ? Date.now() - this.lastMessageAt
                : null,
              messageCount: this.messageCount,
            });
            this.isConnected = false;
            throw error;
          },
          onclose: () => {
            log(`onclose [attempt #${attemptId}]`, {
              messageCount: this.messageCount,
              msSinceOpen: this.lastOpenAt
                ? Date.now() - this.lastOpenAt
                : null,
            });
            this.isConnected = false;
            if (onDone) onDone();
          },
        });
      } catch (error: any) {
        if (this.abortController?.signal.aborted) {
          log(
            `startConnection [attempt #${attemptId}] caught error after abort, ignoring`,
            error,
          );
          return;
        }

        warn(`startConnection [attempt #${attemptId}] caught error`, error);

        if (error?.message === "401") {
          const currentRefreshToken = refreshToken;
          if (!currentRefreshToken) {
            dispatch(resetAuth());
            this.close("401 with no refresh token in catch block");
            return;
          }
          const newTokens = await fetchNewAccessToken(currentRefreshToken);
          if (newTokens?.access_token) {
            log(
              `startConnection [attempt #${attemptId}] retrying after 401 token refresh`,
            );
            dispatch(
              setAuthTokens({
                accessToken: newTokens.access_token,
                refreshToken: newTokens.refresh_token,
              }),
            );
            startConnection(newTokens.access_token).catch((e) =>
              warn("startConnection retry after 401 failed", e),
            );
          } else {
            dispatch(resetAuth());
            this.close("401 refresh returned no access_token");
          }
          return;
        }

        if (onError) onError(error);

        log(
          `startConnection [attempt #${attemptId}] scheduling reconnect in 1000ms`,
        );
        this.reconnectTimeout = setTimeout(() => {
          startConnection(accessToken).catch((e) =>
            warn("scheduled reconnect failed", e),
          );
        }, 1000);
      }
    };

    startConnection(token).then();
  }

  private startStallWatchdog() {
    if (this.stallCheckInterval) {
      clearInterval(this.stallCheckInterval);
    }
    this.stallCheckInterval = setInterval(() => {
      if (!this.isConnected || !this.lastOpenAt) return;

      const reference = this.lastMessageAt ?? this.lastOpenAt;
      const silentFor = Date.now() - reference;

      log("stall watchdog tick", {
        isConnected: this.isConnected,
        silentForMs: silentFor,
        messageCount: this.messageCount,
      });

      if (silentFor > STALL_TIMEOUT_MS) {
        warn(
          `STALL DETECTED - no SSE activity for ${silentFor}ms while isConnected=true. ` +
            `Forcing a reconnect. This is the prime suspect for a single-user stuck-progress bug ` +
            `caused by a proxy/VPN silently buffering the stream.`,
          { messageCount: this.messageCount },
        );
        if (this.currentOptions) {
          const opts = this.currentOptions;
          this.close("stall watchdog forced reconnect");
          this.connect(opts);
        }
      }
    }, STALL_CHECK_INTERVAL_MS);
  }

  close(reason = "manual close") {
    log("close() called", { reason, wasConnected: this.isConnected });
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.stallCheckInterval) {
      clearInterval(this.stallCheckInterval);
      this.stallCheckInterval = null;
    }
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.isConnected = false;
  }
}
